import React from 'react';
import { ListGroup, ListGroupItem, Table } from 'reactstrap';
import moment from 'moment';
import Icon from '@mdi/react';
import { mdiAirplane, mdiDomain } from '@mdi/js';
import {
  clientDetailHeader,
  flightDetailHeader,
  hotelDetailHeader,
} from './constants';
import {
  format2,
  getSelectedFlight,
  getSelectedHotel,
  sum,
} from './SelectedOptionUtil';
import './FinalizedQuote.scss';

function FinalizedQuote({ headerDetails, data }) {
  const { flightDetails, hotelDetails, clientDetails } = data;
  const {
    Flight,
    Hotel,
    Visa,
    AirportTransfer,
    TravelInsurance,
    FlightCost,
    HotelCost,
    VisaCost,
    AirportTransferCost,
    InsuranceCost,
    InsuranceQuoteCost,
    VisaQuoteCost,
    AirportTransferQuoteCost,
  } = headerDetails || {};

  const selectedFlightOption = getSelectedFlight(data);
  const selectedHotelOption = getSelectedHotel(data);

  const isArrayNotEmpty = arr => Array.isArray(arr) && arr.length > 0;

  const totalQuoteCost = sum([
    selectedFlightOption?.headerMeta?.quoteValue,
    selectedHotelOption?.headerMeta?.quoteValue,
    VisaQuoteCost,
    InsuranceQuoteCost,
    AirportTransferQuoteCost,
  ]);

  const totalBookingCost = sum([
    HotelCost,
    FlightCost,
    InsuranceCost,
    VisaCost,
    AirportTransferCost,
  ]);

  const displayValue = (v, fallback = '-') => {
    if (v == null) return fallback;
    if (typeof v === 'string') {
      const t = v.trim();
      return t === '' ? fallback : t;
    }
    return v;
  };

  return (
    <div className="d-flex flex-column gap-4">
      {/* Flight Information */}
      {Flight?.trim() === 'Y' && (
        <div className="d-flex flex-column gap-2">
          <div className="fw-semibold fs-5">Flight Information</div>
          <div className="table-responsive">
            <Table
              id="travel-option-flight-table"
              className="tra-table align-middle text-nowrap table-radius mb-0"
            >
              <thead>
                <tr>
                  {flightDetailHeader.map(header => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {isArrayNotEmpty(flightDetails) ? (
                  flightDetails.map((flight, index) => (
                    <tr key={index}>
                      <td>{flight?.TravelFrom_Description}</td>
                      <td>{flight?.TravelToCountry_Description}</td>
                      <td>{flight?.TravelTo_Description}</td>
                      <td>{moment(flight?.TravelDate).format('L')}</td>
                      <td>
                        {flight?.TravelTime
                          ? moment(flight?.TravelTime, 'HH:mm:ss').format(
                              'hh:mm A',
                            )
                          : '-'}
                      </td>
                      <td>{flight?.Airline_Description}</td>
                      <td>{flight?.TravelClass}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>No flight information found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {/* Hotel Information */}
      {Hotel?.trim() === 'Y' && (
        <div className="d-flex flex-column gap-2">
          <div className="fw-semibold fs-5">Hotel Information</div>
          <div className="table-responsive">
            <Table
              id="travel-option-table"
              className="tra-table align-middle text-nowrap table-radius mb-0"
            >
              <thead>
                <tr>
                  {hotelDetailHeader.map(header => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {isArrayNotEmpty(hotelDetails) ? (
                  hotelDetails.map((hotel, index) => (
                    <tr key={index}>
                      <td>{hotel?.Country_Description}</td>
                      <td>{hotel?.Place}</td>
                      <td>{moment(hotel?.CheckInDate).format('L')}</td>
                      <td>{moment(hotel?.CheckOutDate).format('L')}</td>
                      <td>{hotel?.HotelName}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>No hotel information found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {/* Client Information */}
      {isArrayNotEmpty(clientDetails) && (
        <div className="d-flex flex-column gap-2">
          <div className="fw-semibold fs-5">Client Information</div>
          <div className="table-container">
            <Table
              id="travel-option-table"
              className="tra-table align-middle text-nowrap table-radius mb-0"
            >
              <thead>
                <tr>
                  {clientDetailHeader.map(header => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {clientDetails.map((client, index) => (
                  <tr key={index}>
                    <td>{client?.ClientName}</td>
                    <td>{client?.Company}</td>
                    <td>{client?.ContactNo}</td>
                    <td>{client?.ProjectName}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {/* Selected Information */}
      <div className="approval-lg-wrapper d-flex flex-row align-items-stretch gap-4 selected-info">
        {/* Selected Flight */}
        <ListGroup className="equal-col d-flex flex-column approval-lg">
          <ListGroupItem className="d-flex flex-row align-items-center gap-2 fw-semibold">
            <Icon path={mdiAirplane} size={0.8} />
            <span className="fs-5">Selected Flight</span>
          </ListGroupItem>
          {selectedFlightOption && (
            <ListGroupItem className="d-flex flex-column gap-1">
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">Quote Value</div>
                <div
                  className="d-flex flex-row gap-1 align-items-center approval-info-value"
                  style={{ backgroundColor: '#FFFFCC' }}
                >
                  <span>
                    {displayValue(selectedFlightOption?.headerMeta?.currency)}
                  </span>
                  <span>
                    {displayValue(
                      Number(
                        selectedFlightOption?.headerMeta?.quoteValue,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }),
                    )}
                  </span>
                </div>
              </div>
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">Refund</div>
                <div className="d-flex flex-row gap-1 align-items-center approval-info-value">
                  <span>
                    {displayValue(selectedFlightOption?.headerMeta?.currency)}
                  </span>
                  <span>
                    {displayValue(
                      Number(
                        selectedFlightOption?.headerMeta?.refund,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }),
                    )}
                  </span>
                </div>
              </div>
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">Rebooking</div>
                <div className="approval-info-value">
                  {displayValue(selectedFlightOption?.headerMeta?.rebooking)}
                </div>
              </div>
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">Terms</div>
                <div className="approval-info-value">
                  {displayValue(selectedFlightOption?.headerMeta?.terms)}
                </div>
              </div>
            </ListGroupItem>
          )}
          {isArrayNotEmpty(selectedFlightOption?.option?.segments) ? (
            selectedFlightOption?.option?.segments?.map((segment, index) => (
              <ListGroupItem key={index} className="d-flex flex-column gap-1">
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">Airline</div>
                  <div className="approval-info-value">
                    {segment?.Airline_Description}
                  </div>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">From</div>
                  <div className="approval-info-value">
                    {segment?.TravelFrom_Description}
                  </div>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">To</div>
                  <div className="approval-info-value">
                    {segment?.TravelTo_Description}
                  </div>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">Time</div>
                  <div className="d-flex flex-row gap-1 align-items-center approval-info-value">
                    <span>{moment(segment?.TravelDate).format('L')},</span>
                    <span>
                      {segment?.TravelTime
                        ? moment(segment?.TravelTime, 'HH:mm:ss').format(
                            'hh:mm A',
                          )
                        : '-'}
                    </span>
                  </div>
                </div>
              </ListGroupItem>
            ))
          ) : (
            <ListGroupItem style={{ borderBottom: '0' }}>
              No flights has been selected.
            </ListGroupItem>
          )}
        </ListGroup>
        {/* Selected Hotel */}
        <ListGroup className="equal-col d-flex flex-column approval-lg">
          <ListGroupItem className="d-flex flex-row align-items-center gap-2 fw-semibold">
            <Icon path={mdiDomain} size={0.8} />
            <span className="fs-5">Selected Hotel</span>
          </ListGroupItem>
          {selectedHotelOption && (
            <ListGroupItem className="d-flex flex-column gap-1">
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">Quote Value</div>
                <div
                  className="d-flex flex-row align-items-center gap-1 approval-info-value"
                  style={{ backgroundColor: '#FFFFCC' }}
                >
                  <span>
                    {displayValue(selectedHotelOption?.headerMeta?.currency)}
                  </span>
                  <span>
                    {displayValue(
                      parseFloat(
                        selectedHotelOption?.headerMeta?.quoteValue,
                      ).toFixed(2),
                    )}
                  </span>
                </div>
              </div>
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">
                  Rate Per Night
                </div>
                <div className="d-flex flex-row align-items-center gap-1 approval-info-value">
                  <span>
                    {displayValue(selectedHotelOption?.headerMeta?.currency)}
                  </span>
                  <span>
                    {displayValue(
                      parseFloat(
                        selectedHotelOption?.headerMeta?.ratesPerNight,
                      ).toFixed(2),
                    )}
                  </span>
                </div>
              </div>
              <div className="d-flex flex-row justify-content-between">
                <div className="fw-semibold approval-info-key">Terms</div>
                <div className="approval-info-value">
                  {displayValue(selectedHotelOption?.headerMeta?.terms)}
                </div>
              </div>
            </ListGroupItem>
          )}

          {isArrayNotEmpty(selectedHotelOption?.option?.segments) ? (
            selectedHotelOption.option.segments.map(segment => (
              <ListGroupItem
                key={segment.SrNo}
                className="d-flex flex-column gap-1"
              >
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">Hotel</div>
                  <div className="approval-info-value">
                    {segment?.Country_Description}
                  </div>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">Place</div>
                  <div className="approval-info-value">{segment?.Place}</div>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">Check In</div>
                  <div className="approval-info-value">
                    {moment(segment?.CheckInDate).format('L')}
                  </div>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <div className="fw-semibold approval-info-key">Check Out</div>
                  <div className="approval-info-value">
                    {moment(segment?.CheckOutDate).format('L')}
                  </div>
                </div>
              </ListGroupItem>
            ))
          ) : (
            <ListGroupItem style={{ borderBottom: '0' }}>
              No hotels have been selected.
            </ListGroupItem>
          )}
        </ListGroup>
        {/* Other Information */}
        <ListGroup className="equal-col d-flex flex-column approval-lg">
          <ListGroupItem
            className="d-flex flex-column gap-3"
            style={{ borderBottom: '0' }}
          >
            {/* Travel Insurance */}
            {TravelInsurance.trim() === 'Y' && (
              <>
                <div className="d-flex flex-column gap-2">
                  <div>Travel Insurance Cost (AED)</div>
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid #dedede',
                      padding: '0.35rem',
                      borderRadius: '8px',
                    }}
                  >
                    {displayValue(headerDetails?.InsuranceQuoteCost)}
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  <div>Travel Insurance Comments</div>
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid #dedede',
                      padding: '0.35rem',
                      borderRadius: '8px',
                    }}
                  >
                    {displayValue(headerDetails?.InsuranceComments)}
                  </div>
                </div>
              </>
            )}
            {/* Airport Transfer */}
            {AirportTransfer.trim() === 'Y' && (
              <>
                <div className="d-flex flex-column gap-2">
                  <div>Airport Transfer Cost (AED)</div>
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid #dedede',
                      padding: '0.35rem',
                      borderRadius: '8px',
                    }}
                  >
                    {displayValue(headerDetails?.AirportTransferQuoteCost)}
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  <div>Airport Transfer Comments</div>
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid #dedede',
                      padding: '0.35rem',
                      borderRadius: '8px',
                    }}
                  >
                    {displayValue(headerDetails?.AirportTransferComments)}
                  </div>
                </div>
              </>
            )}
            {/* Visa */}
            {Visa.trim() === 'Y' && (
              <>
                <div className="d-flex flex-column gap-2">
                  <div>Visa Cost (AED)</div>
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid #dedede',
                      padding: '0.35rem',
                      borderRadius: '8px',
                    }}
                  >
                    {displayValue(headerDetails?.VisaQuoteCost)}
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  <div>Visa Comments</div>
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid #dedede',
                      padding: '0.35rem',
                      borderRadius: '8px',
                    }}
                  >
                    {displayValue(headerDetails?.VisaComments)}
                  </div>
                </div>
              </>
            )}
            {/* Budgeted */}
            <div className="d-flex flex-column gap-2">
              <div>Budgeted</div>
              <div
                style={{
                  width: '100%',
                  border: '1px solid #dedede',
                  padding: '0.35rem',
                  borderRadius: '8px',
                }}
              >
                {headerDetails?.Budgeted}
              </div>
            </div>
            {/* Selected Quote Cost */}
            <div className="d-flex flex-column gap-2">
              <div>Selected Quote Cost</div>
              <div
                style={{
                  width: '100%',
                  border: '1px solid #dedede',
                  padding: '0.35rem',
                  borderRadius: '8px',
                }}
              >
                <div>{format2(totalQuoteCost)}</div>
              </div>
            </div>
            {/* Quote Selection */}
            <div className="d-flex flex-column gap-2">
              <div>Quote Selection</div>
              <div
                style={{
                  width: '100%',
                  border: '1px solid #dedede',
                  padding: '0.35rem',
                  borderRadius: '8px',
                  backgroundColor:
                    headerDetails?.EconomicOption === 'Y'
                      ? '#079455'
                      : '#d92d20',
                  fontWeight: '500',
                  color: '#fff',
                }}
              >
                {headerDetails?.EconomicOption === 'Y' ? (
                  <span>Economic</span>
                ) : (
                  <span>Expensive</span>
                )}
              </div>
            </div>
            {/* Economic Quote Cost */}
            <div className="d-flex flex-column gap-2">
              <div>Economic Quote Cost</div>
              <div
                style={{
                  width: '100%',
                  border: '1px solid #dedede',
                  padding: '0.35rem',
                  borderRadius: '8px',
                }}
              >
                {displayValue(
                  Number(headerDetails?.EconomicCost).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  ),
                )}
              </div>
            </div>
            {/* Actual Booking Cost */}
            <div className="d-flex flex-column gap-2">
              <div>Actual Booking Cost</div>
              <div
                style={{
                  width: '100%',
                  border: '1px solid #dedede',
                  padding: '0.35rem',
                  borderRadius: '8px',
                  fontWeight: '500',
                  backgroundColor:
                    totalQuoteCost > totalBookingCost ||
                    totalBookingCost === totalQuoteCost
                      ? '#079455'
                      : '#d92d20',
                }}
              >
                {totalQuoteCost > totalBookingCost ||
                totalBookingCost === totalQuoteCost ? (
                  <span className="text-light">
                    <div>{format2(totalBookingCost)}</div>
                  </span>
                ) : (
                  <span className="text-light">
                    {format2(totalBookingCost)}
                  </span>
                )}
              </div>
            </div>
          </ListGroupItem>
        </ListGroup>
      </div>
      {/* Reasoning of Selection */}
      <ListGroup className="approval-lg">
        <ListGroupItem className="d-flex flex-column gap-2">
          <div>Reason for not selecting the cheapest option</div>
          <div
            style={{
              width: '100%',
              border: '1px solid #dedede',
              padding: '0.35rem',
              borderRadius: '8px',
            }}
          >
            {headerDetails?.QuoteSelectReason || '-'}
          </div>
        </ListGroupItem>
      </ListGroup>
    </div>
  );
}

export default FinalizedQuote;
